import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ReferenceLine,
    ReferenceArea,
    ResponsiveContainer
} from 'recharts';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { useState } from 'react';
import { Box, Button } from '@mui/material';
import { MainCard } from 'component/cards/MainCard2';

const COLORS = [
    '#2196f3',
    '#f44336',
    '#4caf50',
    '#ff9800',
    '#9c27b0',
    '#00bcd4',
    '#795548',
    '#607d8b'
];

const ECG_WINDOW_SECONDS = 10;

const HrChart = ({
    hrPoints,
    maxHrReg,
    maxHr,
    basalHr,
    trainHr,
    minTrainHr,
    exerciseIntervals = [],
    onSelectEcgWindow,
    onGoToEcg
}) => {
    const [isSelectingEcg, setIsSelectingEcg] = useState(false);
    const [ecgSelectionStart, setEcgSelectionStart] = useState(null);

    const renderReferenceLabel =
        (text, color, xOffset = 0) =>
        ({ viewBox }) => {
            const { x, y } = viewBox;

            return (
                <text
                    x={x + xOffset}
                    y={y - 6}
                    fill={color}
                    fontSize={12}
                    fontWeight="bold"
                    textAnchor="start"
                >
                    {text}
                </text>
            );
        };

    const data = hrPoints.map((p, index) => ({
        ...p,
        xIndex: index
    }));

    const startPoint = ecgSelectionStart !== null ? hrPoints[ecgSelectionStart] : null;

    const endPoint =
        ecgSelectionStart !== null
            ? hrPoints[ecgSelectionStart + ECG_WINDOW_SECONDS]
            : null;

    const timeToIndex = Object.fromEntries(hrPoints.map((p, i) => [p.time, i]));

    const labelColorMap = {};

    exerciseIntervals.forEach((interval) => {
        if (!labelColorMap[interval.label]) {
            labelColorMap[interval.label] =
                COLORS[Object.keys(labelColorMap).length % COLORS.length];
        }
    });

    const exerciseLegend = Object.entries(labelColorMap).map(([label, color]) => ({
        label,
        color
    }));

    return (
        <MainCard title={t('records.charts.hrChart')} sx={{ width: '100%' }}>
            {/**<Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                {!isSelectingEcg ? (
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setIsSelectingEcg(true);
                            setEcgSelectionStart(null);
                        }}
                    >
                        {t('ECG.selectECGIntreval')}
                    </Button>
                ) : (
                    <>
                        <Button
                            variant="contained"
                            disabled={!startPoint}
                            onClick={() => {
                                if (!startPoint) return;

                                onSelectEcgWindow({
                                    startIso: startPoint.time,
                                    endIso: endPoint?.time ?? startPoint.time
                                });

                                onGoToEcg();
                                setIsSelectingEcg(false);
                            }}
                        >
                            {t('ECG.viewECG')}
                        </Button>

                        <Button
                            variant="text"
                            color="secondary"
                            onClick={() => {
                                setIsSelectingEcg(false);
                                setEcgSelectionStart(null);
                            }}
                        >
                            {t('ECG.cancel')}
                        </Button>

                        {startPoint && (
                            <Box sx={{ color: 'text.secondary', fontSize: 13 }}>
                                {t('ECG.start')} – <strong>{startPoint.time}</strong>
                                {endPoint && (
                                    <>
                                        {' '}
                                        / {t('ECG.end')} –{' '}
                                        <strong>{endPoint.time}</strong>
                                    </>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </Box>**/}
            <ResponsiveContainer width="100%" height={300}>
                <LineChart
                    data={data}
                    margin={{ top: 20, right: 20, bottom: 20, left: 10 }}
                    onClick={(e) => {
                        if (!isSelectingEcg || !e?.activePayload?.length) return;

                        const index = e.activePayload[0].payload.xIndex;
                        setEcgSelectionStart(index);
                    }}
                >
                    {exerciseIntervals.map((interval, i) => {
                        const color = labelColorMap[interval.label];

                        const x1 = timeToIndex[interval.startTime];
                        const x2 = timeToIndex[interval.endTime];

                        if (x1 == null || x2 == null) return null;

                        return (
                            <ReferenceArea
                                key={i}
                                x1={x1}
                                x2={x2}
                                strokeOpacity={0}
                                fill={color}
                                fillOpacity={0.15}
                            />
                        );
                    })}

                    <XAxis
                        dataKey="xIndex"
                        tickFormatter={(index) => hrPoints[index]?.time}
                        stroke="#999"
                        tickLine={false}
                        axisLine={{ stroke: '#ccc' }}
                    />
                    <YAxis
                        tick={{ fontSize: 12 }}
                        stroke="#999"
                        tickLine={false}
                        axisLine={{ stroke: '#ccc' }}
                        domain={[
                            (dataMin) => Math.min(dataMin, basalHr),
                            (dataMax) =>
                                Math.max(dataMax, maxHr, maxHrReg, trainHr, minTrainHr) +
                                4
                        ]}
                        label={{
                            value: t('records.charts.hrAxisLabel'),
                            angle: -90,
                            position: 'insideLeft',
                            offset: 10,
                            style: {
                                textAnchor: 'middle',
                                fill: '#555',
                                fontSize: 14
                            }
                        }}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: '#ffffff',
                            borderRadius: 8,
                            border: '1px solid #ccc',
                            fontSize: 12
                        }}
                        formatter={(value) => [`${value} Bpm`, 'FC']}
                        labelStyle={{ fontWeight: 500 }}
                    />

                    <Line
                        type="monotone"
                        dataKey="hr"
                        stroke="#1976d2"
                        strokeWidth={3}
                        dot={false}
                        isAnimationActive={false}
                    />
                    <ReferenceLine
                        y={maxHr}
                        stroke="#f44336"
                        strokeDasharray="4 4"
                        label={renderReferenceLabel(
                            `${t('records.charts.hrMax')} (${maxHr} Bpm)`,
                            '#f44336',
                            10
                        )}
                    />
                    <ReferenceLine
                        y={maxHrReg}
                        stroke="#ed7169"
                        strokeDasharray="4 4"
                        label={renderReferenceLabel(
                            `${t('records.charts.hrMaxRecorded')} (${maxHrReg} Bpm)`,
                            '#ed7169',
                            200
                        )}
                    />
                    <ReferenceLine
                        y={trainHr}
                        stroke="#e6c72e"
                        strokeDasharray="4 4"
                        label={renderReferenceLabel(
                            `${t('records.charts.hrTraining')} (${trainHr} Bpm)`,
                            '#e6c72e',
                            400
                        )}
                    />
                    <ReferenceLine
                        y={minTrainHr}
                        stroke="#b5e481"
                        strokeDasharray="4 4"
                        label={renderReferenceLabel(
                            `${t('records.charts.minHrTraining')} (${minTrainHr} Bpm)`,
                            '#b5e481',
                            600
                        )}
                    />
                    <ReferenceLine
                        y={basalHr}
                        stroke="#2c9e00"
                        strokeDasharray="4 4"
                        label={renderReferenceLabel(
                            `${t('records.charts.hrBasal')} (${basalHr} Bpm)`,
                            '#2c9e00',
                            800
                        )}
                    />
                    {isSelectingEcg && ecgSelectionStart !== null && (
                        <ReferenceArea
                            x1={ecgSelectionStart}
                            x2={ecgSelectionStart + ECG_WINDOW_SECONDS}
                            fill="#1976d2"
                            fillOpacity={0.18}
                            strokeOpacity={0}
                        />
                    )}
                </LineChart>
            </ResponsiveContainer>
            {exerciseLegend.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={2} justifyContent="center">
                    {exerciseLegend.map((item) => (
                        <Box key={item.label} display="flex" alignItems="center" gap={1}>
                            <Box
                                sx={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: 0.5,
                                    backgroundColor: item.color,
                                    opacity: 0.5
                                }}
                            />
                            <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
                                {item.label}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
        </MainCard>
    );
};

HrChart.propTypes = {
    hrPoints: PropTypes.array.isRequired,
    maxHrReg: PropTypes.number.isRequired,
    maxHr: PropTypes.number.isRequired,
    basalHr: PropTypes.number.isRequired,
    trainHr: PropTypes.number.isRequired,
    minTrainHr: PropTypes.number.isRequired,
    exerciseIntervals: PropTypes.array
};

export default HrChart;
